import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Copy, 
  Share, 
  Trash2, 
  FileText,
  Eye,
  ExternalLink,
  Loader2,
  BarChart3,
  Code,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FormEmbedCode from '@/components/FormEmbedCode';
import { cn } from '@/lib/utils';

interface FormData {
  id: string;
  title: string;
  description: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  whatsapp_number: string;
}

const MyForms = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [forms, setForms] = useState<FormData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [selectedFormForEmbed, setSelectedFormForEmbed] = useState<FormData | null>(null);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<FormData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const fetchForms = async () => {
      if (!user) return;
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('forms')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setForms(data || []);
      } catch (error) {
        console.error('Error fetching forms:', error);
        toast({ title: "Error loading forms", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchForms();
  }, [user, toast]);

  const filteredForms = forms.filter(form =>
    form.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (form.description && form.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleDuplicateForm = async (formId: string) => {
    try {
      const { data: originalForm } = await supabase.from('forms').select('*').eq('id', formId).single();
      if (!originalForm) return;

      await supabase.from('forms').insert({
        user_id: user?.id,
        title: `${originalForm.title} (Copy)`,
        description: originalForm.description,
        fields: originalForm.fields,
        settings: originalForm.settings,
        whatsapp_number: originalForm.whatsapp_number,
        is_published: false
      });

      toast({ title: "Form duplicated!" });
      window.location.reload();
    } catch (err) {
      toast({ title: "Error duplicating form", variant: "destructive" });
    }
  };

  const handleShareForm = (formId: string) => {
    const formUrl = `${window.location.origin}/form/${formId}`;
    navigator.clipboard.writeText(formUrl);
    toast({ title: "Link copied!" });
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;
    setIsDeleting(true);
    try {
      await supabase.from('forms').delete().eq('id', formToDelete.id);
      toast({ title: "Form deleted" });
      setForms(forms.filter(form => form.id !== formToDelete.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      toast({ title: "Error deleting form", variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>My Forms | Bravax Forms</title>
      </Helmet>

      <div className="max-w-7xl mx-auto p-8 lg:p-12 space-y-12 animate-in fade-in duration-700">
        {/* Editorial Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
               <span className="text-[10px] font-bold text-primary uppercase tracking-widest px-3 py-1 bg-primary/10 rounded-full">Workspace</span>
               <div className="h-px w-8 bg-primary/30" />
            </div>
            <h1 className="text-5xl font-display font-bold tracking-tightest">Your Forms</h1>
            <p className="text-lg text-muted-foreground font-sans max-w-xl leading-relaxed">
              Manage your collection of AI-orchestrated data capture modules.
            </p>
          </div>
          <Button 
            className="btn-gradient h-14 px-8 rounded-2xl gap-3 shadow-2xl shadow-primary/20 hover:scale-[1.02] transition-all" 
            onClick={() => navigate('/form-builder')}
          >
            <Plus className="h-5 w-5" />
            <span>Create New Form</span>
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input
            placeholder="Search through your forms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-16 pl-16 rounded-2xl bg-surface-low border-none text-lg focus:ring-1 focus:ring-primary/20 shadow-inner"
          />
        </div>

        {/* Forms Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32 space-y-4">
             <Loader2 className="h-10 w-10 animate-spin text-primary" />
             <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Scanning database...</p>
          </div>
        ) : filteredForms.length === 0 ? (
          <div className="py-32 rounded-[2.5rem] bg-surface-low/30 border border-dashed border-border/10 flex flex-col items-center justify-center text-center px-12">
             <div className="w-20 h-20 rounded-3xl bg-surface-high flex items-center justify-center mb-8 shadow-2xl">
                <FileText className="h-10 w-10 text-primary/40" />
             </div>
             <h3 className="text-2xl font-display font-bold mb-3">No Modules Found</h3>
             <p className="text-muted-foreground max-w-xs mb-10">
                You haven't initialized any forms yet. Start your journey with the AI Architect.
             </p>
             <Button className="btn-gradient h-12 px-8 rounded-2xl" onClick={() => navigate('/form-builder')}>
                <Sparkles className="h-4 w-4 mr-2" />
                Initialize First Form
             </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredForms.map((form, index) => (
              <Card key={form.id} className="group border-none bg-surface-low/50 hover:bg-surface-low backdrop-blur-xl transition-all duration-500 rounded-[2rem] overflow-hidden shadow-xl hover:shadow-primary/5 flex flex-col animate-in fade-in slide-in-from-bottom-8" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-8 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div className={cn(
                      "h-12 w-12 rounded-2xl flex items-center justify-center shadow-lg",
                      form.is_published ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                       <FileText className="h-6 w-6" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-full hover:bg-surface-high">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-surface-highest border-border/10 rounded-2xl p-2">
                        <DropdownMenuItem onClick={() => navigate(`/form-builder/${form.id}`)} className="rounded-xl p-3">
                          <Edit className="mr-3 h-4 w-4" /> Edit Module
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicateForm(form.id)} className="rounded-xl p-3">
                          <Copy className="mr-3 h-4 w-4" /> Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShareForm(form.id)} className="rounded-xl p-3">
                          <Share className="mr-3 h-4 w-4" /> Copy Access Link
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSelectedFormForEmbed(form); setEmbedDialogOpen(true); }} className="rounded-xl p-3">
                          <Code className="mr-3 h-4 w-4" /> Get Embed Code
                        </DropdownMenuItem>
                        <div className="h-px bg-border/10 my-2" />
                        <DropdownMenuItem onClick={() => { setFormToDelete(form); setDeleteDialogOpen(true); }} className="rounded-xl p-3 text-destructive focus:text-destructive focus:bg-destructive/10">
                          <Trash2 className="mr-3 h-4 w-4" /> Delete Permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-2 mb-8">
                    <div className="flex items-center gap-2">
                       <h3 className="text-xl font-display font-bold tracking-tight line-clamp-1">{form.title}</h3>
                       {form.is_published && <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed h-10">
                      {form.description || "No description provided for this module."}
                    </p>
                  </div>

                  <div className="mt-auto pt-8 border-t border-border/5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 w-full">
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/form-builder/${form.id}`)}
                        className="flex-[2] rounded-2xl h-10 px-4 text-[11px] font-bold uppercase tracking-widest btn-gradient gap-2"
                      >
                         <Edit className="h-3.5 w-3.5" />
                         Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/forms/${form.id}/submissions`)}
                        className="flex-1 rounded-2xl h-10 px-3 text-[11px] font-bold uppercase tracking-widest border border-border/10 bg-surface-high/30 hover:bg-surface-high hover:text-primary transition-all duration-300 gap-2"
                      >
                         <BarChart3 className="h-3.5 w-3.5" />
                         Results
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="rounded-2xl h-10 w-10 border border-border/10 bg-surface-high/30 hover:bg-surface-high hover:text-primary transition-all duration-300"
                        onClick={() => window.open(`${window.location.origin}/form/${form.id}`, '_blank')}
                        title="View Live Form"
                      >
                         <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-col items-end">
                       <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Status</span>
                       <span className={cn("text-xs font-bold uppercase tracking-wider", form.is_published ? "text-primary" : "text-muted-foreground")}>
                          {form.is_published ? 'Live' : 'Draft'}
                       </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialogs... */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-3xl bg-surface-lowest border-border/10 rounded-[2rem] p-10">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-display font-bold tracking-tightest">Embed Code</DialogTitle>
          </DialogHeader>
          {selectedFormForEmbed && (
            <FormEmbedCode 
              formId={selectedFormForEmbed.id}
              formTitle={selectedFormForEmbed.title}
              isPro={user?.plan === 'pro'}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-surface-lowest border-border/10 rounded-[2rem] p-10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-display font-bold text-destructive">Decommission Module?</AlertDialogTitle>
            <AlertDialogDescription className="text-lg text-muted-foreground pt-4">
              This will permanently erase <span className="text-foreground font-bold">"{formToDelete?.title}"</span> and all collected data. This orchestration cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-10">
            <AlertDialogCancel className="rounded-xl border-none bg-surface-high hover:bg-surface-highest">Abort</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteForm} className="rounded-xl bg-destructive hover:bg-destructive/90 text-white px-8">
              {isDeleting ? 'Deleting...' : 'Proceed with Deletion'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default MyForms;